import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Title from '../../components/Title';
import {
  getTopUpBonusesAsync,
  updateTopUpBonusesAsync,
  selectTopUpBonuses,
  selectTopUpBonusesLoading
} from '../../redux/slices/balanceSlice';
import { selectAuth } from '../../redux/slices/authSlice';
import { TopUpBonus } from '../../resources/types/balanceTypes';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#E0E0E0' : '#404040'}`
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2C2C2C'
  }
}));

const TableHeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#FAFAFA' : '#1E1E1E',
  fontWeight: 600,
  fontSize: '14px'
}));

interface BonusFormData {
  pay: string;
  bonus: string;
  text: string;
}

const BonusTopUpBlock: React.FC = () => {
  const dispatch = useDispatch();
  const bonuses = useSelector(selectTopUpBonuses);
  const loading = useSelector(selectTopUpBonusesLoading);
  const auth = useSelector(selectAuth);

  const [open, setOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<BonusFormData>({
    pay: '',
    bonus: '',
    text: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getTopUpBonusesAsync());
  }, [dispatch]);

  const isSuperAdmin = auth.info?.type === 'superAdmin';

  if (!isSuperAdmin) {
    return null;
  }

  const handleOpenDialog = (index?: number) => {
    if (index !== undefined) {
      const bonus = bonuses[index];
      setFormData({
        pay: String(bonus.pay),
        bonus: String(bonus.bonus),
        text: bonus.text || ''
      });
      setEditingIndex(index);
    } else {
      setFormData({
        pay: '',
        bonus: '',
        text: ''
      });
      setEditingIndex(null);
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setFormData({
      pay: '',
      bonus: '',
      text: ''
    });
    setEditingIndex(null);
  };

  const handleInputChange = (field: keyof BonusFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveBonus = () => {
    if (!formData.pay || !formData.bonus) {
      alert('Заповніть обов\'язкові поля');
      return;
    }

    const newBonus: TopUpBonus = {
      pay: Number(formData.pay),
      bonus: Number(formData.bonus),
      text: formData.text || undefined
    };

    let updatedBonuses: TopUpBonus[];
    if (editingIndex !== null) {
      updatedBonuses = bonuses.map((b, i) =>
        i === editingIndex ? newBonus : b
      );
    } else {
      updatedBonuses = [...bonuses, newBonus];
    }

    setSaving(true);
    dispatch(
      updateTopUpBonusesAsync(updatedBonuses, () => {
        handleCloseDialog();
        setSaving(false);
      })
    );
  };

  const handleDeleteBonus = (index: number) => {
    const updatedBonuses = bonuses.filter((_, i) => i !== index);
    setSaving(true);
    dispatch(
      updateTopUpBonusesAsync(updatedBonuses, () => {
        setSaving(false);
      })
    );
  };

  return (
    <Paper
      sx={{
        border: theme =>
          theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
        borderRadius: 2,
        padding: '20px',
        marginTop: 3
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
        <Title variant='h6'>Налаштування бонусів при поповненні</Title>
        <Button
          color='primary'
          startIcon={<AddIcon />}
          variant='contained'
          onClick={() => handleOpenDialog()}
          disabled={loading || saving}
        >
          Додати бонус
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <CircularProgress />
        </Box>
      ) : bonuses.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
          }}
        >
          Бонусів не налаштовано
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Сума поповнення</TableHeaderCell>
                <TableHeaderCell>Бонус</TableHeaderCell>
                <TableHeaderCell>Мітка</TableHeaderCell>
                <TableHeaderCell align='right'>Дії</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bonuses.map((bonus, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell>{bonus.pay}</StyledTableCell>
                  <StyledTableCell>{bonus.bonus}</StyledTableCell>
                  <StyledTableCell>{bonus.text || '-'}</StyledTableCell>
                  <StyledTableCell align='right'>
                    <IconButton
                      size='small'
                      onClick={() => handleOpenDialog(index)}
                      disabled={saving}
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={() => handleDeleteBonus(index)}
                      disabled={saving}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={open} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Редагувати бонус' : 'Додати бонус'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
          <TextField
            label='Сума поповнення'
            type='number'
            value={formData.pay}
            onChange={e => handleInputChange('pay', e.target.value)}
            fullWidth
            disabled={saving}
          />
          <TextField
            label='Бонус'
            type='number'
            value={formData.bonus}
            onChange={e => handleInputChange('bonus', e.target.value)}
            fullWidth
            disabled={saving}
          />
          <TextField
            label='Мітка (напр. Popular)'
            value={formData.text}
            onChange={e => handleInputChange('text', e.target.value)}
            fullWidth
            disabled={saving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Скасувати
          </Button>
          <Button
            onClick={handleSaveBonus}
            variant='contained'
            color='primary'
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BonusTopUpBlock;
